import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Modal, Slide, Stack, Typography, Box, InputLabel, Select, MenuItem, IconButton, FormHelperText } from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";

import { ReactComponent as SdIcon } from "../../../../../../../vendor/_new/icon/services/owasp.svg";
import { ReactComponent as CloseIcon } from "../../../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as InfoIcon } from "../../../../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as ArrowdownIcon } from "../../../../../../../vendor/_new/icon/sites/arrow_down.svg";

import colors from "../../../../../../../utils/colors";
import { GreenSelectForm, GreenSwitch } from "../../../../../../common/Greens";
import { SaveButton, CancelButton } from "../../../../../../common/Button";
import FormHelpTooltip from "../../../../../../common/FormHelpTooltip";
import { UserType, WafAction, WafType, ChallengeKind } from "../../../../../../../utils/constants";
import useAuth from "../../../../../../../hooks/useAuth";
import useWAFConfig from "../../../../../../../hooks/user/useWAFConfig";
import { useNotification } from "../../../../../../../contexts/NotificationContext";
import { validateWafSettingsForm } from "../../../../../../../utils/validation";
import { useDrawerBaselineDirty, useGuardedDrawerClose } from "../../../../super/design";

const helperTextSx = { fontSize: 12, color: colors.neutral[600], mx: 0, mt: 0.5 };

const menuItemSx = {
  height: 38,
  fontSize: 14,
  lineHeight: "21px",
  "&:hover": { color: colors.primary[300] },
};

const ConfigSDWAFSettingsModal = ({ siteUid, open, handleClose }) => {
  const { userType } = useAuth();
  const { wafConfig, configWafSetting, getWAFConfig } = useWAFConfig();
  const { showSuccess, showError } = useNotification();
  const [saving, setSaving] = React.useState(false);
  const [sd_sig_module_active, set_sd_sig_module_active] = React.useState(false);
  const [waf_action_sd_sig, set_waf_action_sd_sig] = React.useState(WafAction.DETECT);
  // signatureWafLevel: 1..=4 sensitivity for the Layerd Signature WAF engine.
  // Default 1 (Conservative) per the data-plane contract; backend column is
  // `waf_configs.signatureWafLevel`, PATCH key is `signature_waf_level`.
  const [signature_waf_level, set_signature_waf_level] = React.useState(1);
  // wafChallengeKindSdSig: nullable challenge variant; only meaningful when
  // the action is Challenge. NULL in UI state means "let backend default
  // (managed)"; PATCH key is `waf_challenge_kind_sd_sig`.
  const [waf_challenge_kind_sd_sig, set_waf_challenge_kind_sd_sig] = React.useState(null);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [baselineKey, setBaselineKey] = useState(0);

  const formValidation = useMemo(
    () =>
      validateWafSettingsForm({
        variant: "signature",
        waf_action: waf_action_sd_sig,
        signature_waf_level,
        waf_challenge_kind_sd_sig,
      }),
    [waf_action_sd_sig, signature_waf_level, waf_challenge_kind_sd_sig],
  );

  const touchField = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const showFieldErr = useCallback(
    (field) => (submitAttempted || touched[field]) && formValidation.errors[field],
    [submitAttempted, touched, formValidation.errors],
  );

  useEffect(() => {
    if (!open) {
      setTouched({});
      setSubmitAttempted(false);
    }
  }, [open]);

  useEffect(() => {
    if (wafConfig) {
      set_sd_sig_module_active(wafConfig?.sd_sig_module_active);
      set_waf_action_sd_sig(wafConfig?.waf_action_sd_sig);
      set_signature_waf_level(wafConfig?.signature_waf_level || 1);
      set_waf_challenge_kind_sd_sig(wafConfig?.waf_challenge_kind_sd_sig ?? null);
      if (open) setBaselineKey((k) => k + 1);
    }
  }, [wafConfig, open]);

  const formSnapshot = useMemo(
    () => ({
      sd_sig_module_active,
      waf_action_sd_sig,
      signature_waf_level,
      waf_challenge_kind_sd_sig,
    }),
    [sd_sig_module_active, waf_action_sd_sig, signature_waf_level, waf_challenge_kind_sd_sig],
  );
  const isDirty = useDrawerBaselineDirty(open, formSnapshot, baselineKey);
  const { requestClose, confirmDialog } = useGuardedDrawerClose(handleClose, isDirty, { disabled: saving });

  const handleSave = async () => {
    setSubmitAttempted(true);
    const validation = validateWafSettingsForm({
      variant: "signature",
      waf_action: waf_action_sd_sig,
      signature_waf_level,
      waf_challenge_kind_sd_sig,
    });
    if (!validation.valid) return;

    setSaving(true);
    if (userType !== UserType.ORG_USER) {
      try {
        const tasks = [];
        if (wafConfig?.sd_sig_module_active !== sd_sig_module_active) {
          tasks.push(() => configWafSetting(siteUid, "sdSigWafActiveChange", { enable: sd_sig_module_active }));
        }
        if (wafConfig?.waf_action_sd_sig !== waf_action_sd_sig) {
          tasks.push(() =>
            configWafSetting(siteUid, "sdSigWafActionChange", { cate_id: WafType.SENSEDEFENCE_SIGNATURE, action: waf_action_sd_sig })
          );
        }
        if (wafConfig?.signature_waf_level !== signature_waf_level) {
          tasks.push(() => configWafSetting(siteUid, "sigWafLevelChange", { level: signature_waf_level }));
        }
        // Only relevant when action is Challenge; the picker is hidden otherwise
        // so we won't accidentally PATCH a stale kind for Detect/Block.
        if (waf_action_sd_sig === WafAction.CHALLENGE &&
            (wafConfig?.waf_challenge_kind_sd_sig ?? null) !== waf_challenge_kind_sd_sig) {
          tasks.push(() =>
            configWafSetting(siteUid, "sdSigChallengeKindChange", { challenge_kind: waf_challenge_kind_sd_sig })
          );
        }
        if (tasks.length > 0) {
          for (const task of tasks) {
            await task();
          }
          await getWAFConfig(siteUid);
          showSuccess("Layerd Signature WAF settings saved successfully");
        } else {
          showSuccess("No changes to save");
        }
      } catch (err) {
        console.error(err);
        showError("Failed to save Layerd Signature WAF settings");
      }
    }
    setSaving(false);
    handleClose();
  };

  return (
    <>
    <Modal 
      open={open} 
      onClose={requestClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
        }
      }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 480,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
          }}
        >
          <Stack direction={"row"} justifyContent={"space-between"} spacing={6} sx={{ px: 4, pt: 4, pb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  backgroundColor: "#EEF0FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <SdIcon style={{ width: 32, height: 32, color: "#6371F7" }} />
              </Box>
              <Stack>
                <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap" }}>
                  <Typography
                    sx={ { fontWeight: "medium", fontSize: 20, lineHeight: "30px", color: colors.neutral[1000] }}
                  >
                    Layerd Signature WAF settings
                  </Typography>
                  <FormHelpTooltip tooltip="Configure the Layerd Signature WAF engine: enable/disable, paranoia level, default action, and per-rule-group overrides for known attack signatures." />
                </Stack>
                <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                  Customize how Layerd AI protects this application from threats.
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={requestClose} aria-label="Close" sx={{ p: 0 }}>
              <CloseIcon sx={{ color: colors.neutral[600] }} />
            </IconButton>
          </Stack>
          <Box sx={{ py: 4, pl: 4, height: `calc(100vh - 130px)` }}>
            <ReactPerfectScrollbar>
              <Stack justifyContent={"space-between"} spacing={4} sx={{ pr: 4, height: "100%" }}>
                <Stack spacing={4}>
                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    spacing={3}
                    justifyContent={"space-between"}
                    sx={{ borderRadius: "8px", border: `solid 1px ${colors.neutral[300]}`, backgroundColor: "white", p: 3 }}
                  >
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                      Layerd Signature WAF
                    </Typography>
                    <GreenSwitch
                      disabled={saving}
                      checked={sd_sig_module_active}
                      onChange={(e) => set_sd_sig_module_active(e.target.checked)}
                    />
                  </Stack>
                  <GreenSelectForm variant="outlined" fullWidth error={Boolean(showFieldErr("waf_action"))}>
                    <InputLabel id="waf_action">WAF Action</InputLabel>
                    <Select
                      labelId="waf_action"
                      IconComponent={ArrowdownIcon}
                      disabled={saving}
                      value={waf_action_sd_sig}
                      onClose={() => touchField("waf_action")}
                      onChange={(e) => set_waf_action_sd_sig(parseInt(e.target.value))}
                    >
                      <MenuItem value={WafAction.DETECT} key={`SDWafAction${WafAction.DETECT}`} sx={menuItemSx}>
                        Detect
                      </MenuItem>
                      <MenuItem value={WafAction.BLOCK} key={`SDWafAction${WafAction.BLOCK}`} sx={menuItemSx}>
                        Block
                      </MenuItem>
                      <MenuItem value={WafAction.CHALLENGE} key={`SDWafAction${WafAction.CHALLENGE}`} sx={menuItemSx}>
                        Challenge
                      </MenuItem>
                    </Select>
                    {showFieldErr("waf_action") && (
                      <FormHelperText error sx={helperTextSx}>{formValidation.errors.waf_action}</FormHelperText>
                    )}
                  </GreenSelectForm>
                  <GreenSelectForm variant="outlined" fullWidth error={Boolean(showFieldErr("signature_waf_level"))}>
                    <InputLabel id="sd_sig_sensitivity">WAF Sensitivity</InputLabel>
                    <Select
                      labelId="sd_sig_sensitivity"
                      IconComponent={ArrowdownIcon}
                      disabled={saving}
                      value={signature_waf_level}
                      onClose={() => touchField("signature_waf_level")}
                      onChange={(e) => set_signature_waf_level(parseInt(e.target.value))}
                    >
                      <MenuItem value={1} key="SDWafLevel1" sx={menuItemSx}>
                        1 - Conservative
                      </MenuItem>
                      <MenuItem value={2} key="SDWafLevel2" sx={menuItemSx}>
                        2 - Balanced
                      </MenuItem>
                      <MenuItem value={3} key="SDWafLevel3" sx={menuItemSx}>
                        3 - Aggressive
                      </MenuItem>
                      <MenuItem value={4} key="SDWafLevel4" sx={menuItemSx}>
                        4 - Paranoid
                      </MenuItem>
                    </Select>
                    {showFieldErr("signature_waf_level") && (
                      <FormHelperText error sx={helperTextSx}>{formValidation.errors.signature_waf_level}</FormHelperText>
                    )}
                  </GreenSelectForm>
                  {waf_action_sd_sig === WafAction.CHALLENGE && (
                    <GreenSelectForm variant="outlined" fullWidth error={Boolean(showFieldErr("waf_challenge_kind_sd_sig"))}>
                      <InputLabel id="sd_sig_challenge_kind" shrink>Challenge kind</InputLabel>
                      <Select
                        labelId="sd_sig_challenge_kind"
                        label="Challenge kind"
                        IconComponent={ArrowdownIcon}
                        disabled={saving}
                        value={waf_challenge_kind_sd_sig ?? ""}
                        onClose={() => touchField("waf_challenge_kind_sd_sig")}
                        onChange={(e) => {
                          const v = e.target.value;
                          set_waf_challenge_kind_sd_sig(v === "" ? null : v);
                        }}
                        displayEmpty
                      >
                        <MenuItem value="" key="SDSigChallengeKindDefault" sx={menuItemSx}>
                          Default (Managed)
                        </MenuItem>
                        <MenuItem value={ChallengeKind.INVISIBLE} key="SDSigChallengeKindInvisible" sx={menuItemSx}>
                          Invisible
                        </MenuItem>
                        <MenuItem value={ChallengeKind.MANAGED} key="SDSigChallengeKindManaged" sx={menuItemSx}>
                          Managed
                        </MenuItem>
                        <MenuItem value={ChallengeKind.ADAPTIVE} key="SDSigChallengeKindAdaptive" sx={menuItemSx}>
                          Adaptive
                        </MenuItem>
                        <MenuItem value={ChallengeKind.INTERACTIVE} key="SDSigChallengeKindInteractive" sx={menuItemSx}>
                          Interactive
                        </MenuItem>
                      </Select>
                      {showFieldErr("waf_challenge_kind_sd_sig") && (
                        <FormHelperText error sx={helperTextSx}>{formValidation.errors.waf_challenge_kind_sd_sig}</FormHelperText>
                      )}
                    </GreenSelectForm>
                  )}
                  <Stack
                    direction={"row"}
                    spacing={2}
                    sx={{
                      minHeight: 48,
                      p: 2,
                      borderRadius: "8px",
                      backgroundColor: colors.primary["alpha"],
                      border: `solid 1px ${colors.primary[300]}`,
                    }}
                  >
                    <Box sx={{ width: 24, height: 24 }}>
                      <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                    </Box>
                    <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                      You can update, review, or manage settings on this screen. Changes will be applied instantly after save!
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction={"row"} alignItems={"center"} spacing={3}>
                  <SaveButton onClick={handleSave} disabled={saving} />
                  <CancelButton onClick={requestClose} />
                </Stack>
              </Stack>
            </ReactPerfectScrollbar>
          </Box>
        </Stack>
      </Slide>
    </Modal>
    {confirmDialog}
    </>
  );
};
export default ConfigSDWAFSettingsModal;
